/**
 * profanityFilter.js
 *
 * Türkçe + İngilizce küfür / cinsel / ırkçı / hakaret filtresi (Roblox tarzı maskeleme).
 * Kelime listeleri için bkz. ./profanityWords.js
 *
 * Kullanım:
 *   import { filterText, hasProfanity } from '@/utils/profanityFilter';
 *   filterText('p0rn0 izle')                          // '***** izle'
 *   filterText('s.i.k.t.i.r', { fixedLength: true })  // '***'
 *   hasProfanity('merhaba')                           // false
 *
 * Atlatma yöntemleri:
 *   - Rakam/sembol:        p0rn0, s1kt1r, a55, a$$hole, n!gga, 8itch
 *   - Harf tekrarı:        siiiiktir, fuuuuck
 *   - Araya boşluk/nokta:  s i k t i r, s.i.k.t.i.r, s_i_k_t_i_r
 *   - Parçalı yazım:       sik.tir, fu-ck, a.mcik   (nokta / tire / alt çizgi ile bölünenler)
 *                          si ktir, si k tir, s ik, fu ck   (boşlukla 2-5 parçaya bölünenler)
 *   - Yıldızlı yazım:      f*ck, sh*t, s*ktir
 *   - Benzer karakterler:  Kiril/Yunan lookalike harfler (а, е, о, р...), tam genişlik harfler (ｓｉｋ),
 *                          aksanlı harfler (fúck), sıfır genişlikli / birleştirici karakterler
 *   - Çok kelimeli:        "kill yourself", "amina koyim", "it oglu it" gibi kalıplar
 */

import { CORE, MILD, DIACRITIC, SPLIT_EXTRA, NO_SPLIT } from './profanityWords';

/* ------------------------------------------------------------------ */
/* Normalizasyon                                                        */
/* ------------------------------------------------------------------ */

const CHAR_MAP = {
  ı: 'i', İ: 'i', I: 'i', ş: 's', Ş: 's', ç: 'c', Ç: 'c', ğ: 'g', Ğ: 'g',
  ö: 'o', Ö: 'o', ü: 'u', Ü: 'u', â: 'a', Â: 'a', î: 'i', Î: 'i', û: 'u', Û: 'u',
  ø: 'o', Ø: 'o',
  0: 'o', 1: 'i', 3: 'e', 4: 'a', 5: 's', 7: 't', 8: 'b', '€': 'e', '¡': 'i',
};

// Kiril / Yunan "benzer görünen" harfler
const LOOKALIKE = {
  а: 'a', е: 'e', о: 'o', р: 'p', с: 'c', х: 'x', у: 'y', і: 'i', ѕ: 's', ј: 'j', к: 'k', м: 'm',
  н: 'h', т: 't', в: 'b', α: 'a', ε: 'e', ι: 'i', κ: 'k', ν: 'v', ο: 'o', ρ: 'p', τ: 't', υ: 'u',
  χ: 'x',
};

// Bu işaretler yalnızca harflerin yanındayken harfe çevrilir ("ass!" -> "ass" kalır)
const CONTEXT_CHARS = { '!': 'i', $: 's', '@': 'a' };

const ALNUM = /[\p{L}\p{N}]/u;
// Sıfır genişlikli, yön kontrol ve birleştirici (zalgo / üstü çizili) karakterler yok sayılır
const IGNORABLE = /[\u00AD\u200B-\u200F\u202A-\u202E\u2060-\u2064\uFEFF\p{M}]/u;

function foldChar(ch) {
  const direct = CHAR_MAP[ch];
  if (direct) return direct;
  if (ch.charCodeAt(0) > 127) {
    const k = ch.normalize('NFKC'); // tam genişlik vb. -> ASCII
    const c = k.length === 1 ? k : ch;
    let low = c.toLowerCase();
    if (low.length !== 1) low = c;
    if (CHAR_MAP[low]) return CHAR_MAP[low];
    if (LOOKALIKE[low]) return LOOKALIKE[low];
    const d = low.normalize('NFD'); // é -> e
    if (d.length > 1 && d.charCodeAt(0) < 128) return d[0];
    return low;
  }
  return ch.toLowerCase();
}

/**
 * Normalize metin + konum haritası döndürür.
 * norm[i] karakteri, orijinal metindeki map[i] konumundan gelir (yok sayılanlar atlanır).
 */
function normalize(src) {
  let norm = '';
  const map = [];
  for (let i = 0; i < src.length; i++) {
    const ch = src[i];
    if (ch.charCodeAt(0) > 127 && IGNORABLE.test(ch)) continue;

    let out;
    const ctx = CONTEXT_CHARS[ch];
    if (ctx) {
      // yan yana semboller de (a$$) harf sayılır
      const prev = i > 0 && (ALNUM.test(src[i - 1]) || src[i - 1] in CONTEXT_CHARS);
      const next = i < src.length - 1 && (ALNUM.test(src[i + 1]) || src[i + 1] in CONTEXT_CHARS);
      out = (ch === '!' ? prev && next : prev || next) ? ctx : ch;
    } else {
      out = foldChar(ch);
    }
    norm += out;
    map.push(i);
  }
  return { norm, map };
}

/** Kural kelimesini regex'e çevirir: tekil harf -> "x+", çift harf -> "x{2,}" */
const fuzzy = (word) =>
  word.replace(/([a-z])\1*/g, (run, c) => (run.length > 1 ? `${c}{${run.length},}` : `${c}+`));

const toWord = (w) => normalize(String(w).normalize('NFC')).norm.replace(/[^a-z]/g, '');

/* ------------------------------------------------------------------ */
/* Sabitler                                                             */
/* ------------------------------------------------------------------ */

const TR_SUFFIX =
  '(?:ler|lar|leri|lari|lik|luk|ligi|lugu|in|un|i|u|e|a|den|dan|ten|tan|de|da|im|um|le|la|ci|cu|si|su|sin|siz|ce|ca|m|n|y)';

const TOKEN_RE = /[a-z]+(?:\*[a-z]+)*/g; // "f*ck" tek kelime sayılır
const SPACED_RE = /(?:^|[^a-z])([a-z](?:[\s._*-]+[a-z]){2,})(?![a-z])/g; // "s i k t i r", "s.i.k"
const JOINED_RE = /[a-z]+(?:[._-]+[a-z]+)+/g; // "sik.tir", "fu-ck"
const PHRASE_GAP_RE = /^[\s.,_-]{1,3}$/;
const SPLIT_GAP_RE = /^[ \t_-]{1,3}$/; // parçalar arasında yalnızca boşluk / tire / alt çizgi
const APOSTROPHES = "'’`";
const SPLIT_MAX_TOKENS = 5;
const SPLIT_MAX_LEN = 24;
const VOWELS = ['a', 'e', 'i', 'o', 'u'];
const CACHE_LIMIT = 5000;

/* ------------------------------------------------------------------ */
/* Filtre fabrikası                                                     */
/* ------------------------------------------------------------------ */

/**
 * @param {object}   [config]
 * @param {string[]} [config.extraWords]   Ek yasaklı kökler (Türkçe ek alabilir)
 * @param {string[]} [config.allowWords]   Asla sansürlenmeyecek tam kelimeler (beyaz liste)
 * @param {string}   [config.maskChar]     Maske karakteri, varsayılan '*'
 * @param {boolean}  [config.fixedLength]  true ise her eşleşme sabit 3 karakter olur (***)
 * @param {boolean}  [config.includeMild]  Hafif hakaret / yumuşak cinsel kelimeler de engellensin mi (varsayılan true)
 */
export function createProfanityFilter({
  extraWords = [],
  allowWords = [],
  maskChar = '*',
  fixedLength = false,
  includeMild = true,
} = {}) {
  const tiers = includeMild ? [CORE, MILD] : [CORE];
  const gather = (key) => tiers.flatMap((t) => t[key] || []);
  const alt = (list) => list.map(fuzzy).join('|');

  const extra = extraWords.map(toWord).filter(Boolean);
  const allow = new Set(allowWords.map(toWord).filter(Boolean));

  const substrRe = new RegExp(alt(gather('substr')));
  const prefixRe = new RegExp(`^(?:${alt(gather('prefix'))})`);
  const stemRe = new RegExp(`^(?:${alt([...gather('stem'), ...extra])})${TR_SUFFIX}{0,3}$`);
  const exactRe = new RegExp(`^(?:${alt(gather('exact'))})(?:es|s)?$`);

  // Parçalı yazım için: birleşince TAM kelime olması gerekir (önek/içerme yok)
  const noSplit = new Set(NO_SPLIT);
  const splittable = [
    ...gather('substr'), ...gather('prefix'), ...gather('stem'), ...extra, ...SPLIT_EXTRA,
  ].filter((w) => w.length >= 4 && !noSplit.has(w));
  const wholeRe = new RegExp(`^(?:${alt(splittable)})${TR_SUFFIX}{0,3}(?:es|s)?$`);

  const diacRules = DIACRITIC.map((r) => ({
    re: new RegExp(
      r.kind === 'stem' ? `^(?:${alt(r.words)})${TR_SUFFIX}{0,3}$` : `^(?:${alt(r.words)})$`,
    ),
    needs: r.needs,
    not: r.not,
  }));

  const phrases = gather('phrases').map((p) =>
    p.map((el) => {
      const isPrefix = el.endsWith('*');
      const base = isPrefix ? el.slice(0, -1) : el;
      return new RegExp(`^${fuzzy(base)}${isPrefix ? '' : '$'}`);
    }),
  );

  // Aynı kelimeyi tekrar tekrar regex'e sokmamak için küçük önbellek
  const cache = new Map();
  function coreBad(w) {
    const hit = cache.get(w);
    if (hit !== undefined) return hit;
    const res = exactRe.test(w) || stemRe.test(w) || prefixRe.test(w) || substrRe.test(w);
    if (cache.size >= CACHE_LIMIT) cache.clear();
    cache.set(w, res);
    return res;
  }

  const diacBad = (w, orig) =>
    diacRules.some((r) => r.re.test(w) && (!r.needs || r.needs.test(orig)) && (!r.not || !r.not.test(orig)));

  const testWord = (w, orig) => !allow.has(w) && (coreBad(w) || diacBad(w, orig));

  function isBadToken(token, orig) {
    if (!token.includes('*')) return testWord(token, orig);
    // "f*ck" -> yıldızı sesli harflerle dene (en fazla 2 yıldız)
    const stars = token.split('*').length - 1;
    if (stars > 2) return false;
    let variants = [token];
    for (let i = 0; i < stars; i++) {
      variants = variants.flatMap((v) => VOWELS.map((ch) => v.replace('*', ch)));
    }
    return variants.some((v) => testWord(v, orig));
  }

  /** Orijinal metindeki sansürlenecek [başlangıç, bitiş) aralıklarını döndürür. */
  function findRanges(src) {
    if (!src) return [];
    const { norm, map } = normalize(src);
    if (!norm) return [];

    const ranges = [];
    const push = (s, e) => ranges.push([map[s], map[e - 1] + 1]);

    // 1) Tek kelimeler
    const tokens = [];
    for (const m of norm.matchAll(TOKEN_RE)) {
      const s = m.index;
      const e = s + m[0].length;
      const bad = isBadToken(m[0], src.slice(map[s], map[e - 1] + 1));
      if (bad) push(s, e);
      // "don't it" gibi kısaltma parçaları ("t") birleştirmeye girmesin
      const apo = APOSTROPHES.includes(norm[s - 1] || ' ') || APOSTROPHES.includes(norm[e] || ' ');
      tokens.push({ w: m[0], s, e, bad, apo });
    }

    // 2) Çok kelimeli kalıplar ("kill yourself", "amina koyim")
    if (tokens.length > 1) {
      for (let i = 0; i < tokens.length; i++) {
        for (const p of phrases) {
          if (i + p.length > tokens.length || !p[0].test(tokens[i].w)) continue;
          let ok = true;
          for (let j = 1; j < p.length; j++) {
            const a = tokens[i + j - 1];
            const b = tokens[i + j];
            if (!PHRASE_GAP_RE.test(norm.slice(a.e, b.s)) || !p[j].test(b.w)) {
              ok = false;
              break;
            }
          }
          if (ok) push(tokens[i].s, tokens[i + p.length - 1].e);
        }
      }
    }

    // 2b) Boşlukla parçalanmış kelimeler: "si ktir", "si k tir git", "s ik", "fu ck"
    //     Yalnızca birleşimi TAM bir yasaklı kelime ise ve parçalardan biri en fazla 3 harfliyse.
    for (let i = 0; i < tokens.length - 1; i++) {
      const first = tokens[i];
      if (first.apo || first.w.includes('*')) continue;
      let joined = first.w;
      let minLen = first.w.length;
      for (let j = i + 1; j < tokens.length && j < i + SPLIT_MAX_TOKENS; j++) {
        const t = tokens[j];
        if (t.apo || t.w.includes('*')) break;
        if (!SPLIT_GAP_RE.test(norm.slice(tokens[j - 1].e, t.s))) break;
        joined += t.w;
        if (joined.length > SPLIT_MAX_LEN) break;
        minLen = Math.min(minLen, t.w.length);
        if (minLen > 3 || allow.has(joined)) continue;
        const orig = src.slice(map[first.s], map[t.e - 1] + 1);
        if (wholeRe.test(joined) || diacBad(joined, orig)) push(first.s, t.e);
      }
    }

    // 3) Tek harflerle bölünmüş: "s i k t i r", "s.i.k.t.i.r"
    for (const m of norm.matchAll(SPACED_RE)) {
      const run = m[1];
      const s = m.index + m[0].length - run.length;
      if (testWord(run.replace(/[^a-z]/g, ''), run)) push(s, s + run.length);
    }

    // 4) Nokta / tire / alt çizgi ile parçalanmış: "sik.tir", "fu-ck"
    for (const m of norm.matchAll(JOINED_RE)) {
      const parts = [...m[0].matchAll(/[a-z]+/g)].map((p) => ({
        w: p[0],
        s: m.index + p.index,
        e: m.index + p.index + p[0].length,
      }));
      for (let i = 0; i < parts.length - 1; i++) {
        for (let n = 2; n <= 3 && i + n <= parts.length; n++) {
          const win = parts.slice(i, i + n);
          if (win.some((p) => coreBad(p.w))) continue; // zaten tek başına yakalanıyor
          const joined = win.map((p) => p.w).join('');
          if (!allow.has(joined) && (prefixRe.test(joined) || substrRe.test(joined))) {
            push(win[0].s, win[n - 1].e);
          }
        }
      }
    }

    // Çakışan aralıkları birleştir
    ranges.sort((a, b) => a[0] - b[0]);
    const merged = [];
    for (const r of ranges) {
      const last = merged[merged.length - 1];
      if (last && r[0] <= last[1]) last[1] = Math.max(last[1], r[1]);
      else merged.push([r[0], r[1]]);
    }
    return merged;
  }

  const prepare = (text) => (text == null ? '' : String(text).normalize('NFC'));

  /** Metni silmez/engellemez; küfürlü kısımları maskeleyip temizlenmiş metni döndürür. */
  function filterText(text, options = {}) {
    const src = prepare(text);
    const ranges = findRanges(src);
    if (ranges.length === 0) return src;

    const mc = options.maskChar ?? maskChar;
    const fixed = options.fixedLength ?? fixedLength;

    let out = '';
    let pos = 0;
    for (const [s, e] of ranges) {
      out += src.slice(pos, s) + (fixed ? mc.repeat(3) : mc.repeat(e - s));
      pos = e;
    }
    return out + src.slice(pos);
  }

  /** Metinde sansürlenecek bir şey var mı? */
  function hasProfanity(text) {
    return findRanges(prepare(text)).length > 0;
  }

  /** Eşleşmeleri konumlarıyla döndürür (arayüzde vurgulamak / loglamak için). */
  function findProfanity(text) {
    const src = prepare(text);
    return findRanges(src).map(([start, end]) => ({ start, end, match: src.slice(start, end) }));
  }

  return { filterText, hasProfanity, findProfanity };
}

// Bileşenlerde doğrudan kullanılacak hazır örnek
const defaultFilter = createProfanityFilter();

export const filterText = defaultFilter.filterText;
export const hasProfanity = defaultFilter.hasProfanity;
export const findProfanity = defaultFilter.findProfanity;

export default defaultFilter;
